import type {Weekday} from '#lit/common/constants/dates.js';
import type {ISOString, Nullable} from '#lit/types/general.js';
import {isDefined, isString} from '#lit/utils/index.js';
import {findItem} from '#lit/utils/arrays.js';
import type {Timezone} from '#lit/types/timezone.js';

export type ScheduleTime = string & {__kind__: 'schedule-time'};

export enum JourneyScheduleType {
  ONE_TIME = 'One Time',
  ALWAYS_ON = 'Always On',
  RECURRING_SCHEDULED = 'Recurring Scheduled',
  MANUAL = 'Manual',
}

export enum JourneyType {
  STANDARD = 'Standard',
  REALTIME = 'Realtime',
}

export enum JourneyStartType {
  ON_PUBLISH = 'On Publish',
  SCHEDULE = 'Schedule',
}

export enum JourneyEndType {
  NEVER = 'Never',
  SCHEDULE = 'Schedule',
}

export enum Frequency {
  MONTHLY = 'monthly',
  BI_WEEKLY = 'bi-weekly',
  WEEKLY = 'weekly',
  DAILY = 'daily',
  /* YET UNSUPPORTED */
  // YEARLY = 'yearly',
  // HOURLY = 'hourly',
  // MINUTELY = 'minutely',
  // SECONDLY = 'secondly',
}

export enum ByType {
  /**
   * SU, MO, TU, WE, TH, FR, SA
   * *It includes Saturday and Sunday, do not confuse with workdays*
   */
  WEEK_DAY = 'weekday',

  // YET UNSUPPORTED
  /* 1 to 12 (depending on the month, of course) */
  // MONTH = 'month',
  /* 1 to 31 (depending on the month, of course) */
  MONTH_DAY = 'month-day',
  /* 1 to 365/366 (depending on the year, of course) */
  // YEAR_DAY = 'year-day',
  /* 1 to 52 (depending on the year, of course) */
  // WEEK_NUMBER = 'week-number',
  /* 0 to 23 (rest of division by 24) */
  // HOUR = 'hour',
  /* 0 to 59 (rest of division by 60) */
  // MINUTE = 'minute',
  /* 0 to 59 (rest of division by 60) */
  // SECOND = 'second',
}

/**
 * Based on RRule
 * {@link https://jakubroztocil.github.io/rrule/}
 */
// DEPRECATING: PLEASE USE @frontend/packages/types/journeys.ts
export type JourneyScheduleData = {
  schedule_type: Nullable<string>;
  frequency: Nullable<string>;
  occurrence?: Array<{type: string; value: number[]}>;
  start_type: Nullable<string>;
  start_time?: Nullable<{
    time?: ISOString;
    timezone: string;
    timezoneOffset: string;
  }>;
  end_type: Nullable<string>;
  end_time?: Nullable<{
    time?: ISOString;
    timezone: string;
    timezoneOffset: string;
  }>;
  /* Yet unsupported */
  // count?: number;
  // interval?: number;
};

export type AudienceScheduleOccurrence = {
  type: ByType.WEEK_DAY | ByType.MONTH_DAY;
  values: Weekday[];
  bySetPos?: Nullable<number[]>;
};

export interface IScheduleOccurrence {
  get(type: ByType.WEEK_DAY): Weekday[];
  get(type: ByType): unknown;
  set(type: ByType.WEEK_DAY, value: Weekday[]): void;
  set(type: ByType, value: unknown): void;
  daily(): void;
  clear(): void;
}

export interface IJourneySchedule {
  type: Nullable<JourneyScheduleType>;
  timezone: Timezone;
  start: Nullable<{
    type: JourneyStartType;
    time: Nullable<Date>;
  }>;
  end: Nullable<{
    type: JourneyEndType;
    time: Nullable<Date>;
  }>;
  frequency: Nullable<Frequency>;
  occurrence: IScheduleOccurrence;

  // YET UNSUPPORTED
  // count?: number;
  // interval?: number;
}

/** TYPE GUARDS */
function createTypeGuard<T>(items: T[]) {
  return (value: unknown): value is T => {
    if (!value || !isString(value)) return false;
    const item = findItem(items as string[], value, v => v);
    return isDefined(item);
  };
}

export const isJourneyScheduleType = createTypeGuard(
  Object.values(JourneyScheduleType)
);

export const isJourneyStartType = createTypeGuard(
  Object.values(JourneyStartType)
);

export const isJourneyEndType = createTypeGuard(Object.values(JourneyEndType));

export const isJourneyFrequency = createTypeGuard(Object.values(Frequency));

export const isJourneyOccurrenceType = createTypeGuard(Object.values(ByType));
